import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Plus, RefreshCw, ShieldCheck, Eye, UploadCloud, Loader2, CheckCircle2, XCircle, ArrowLeft, Lock,
} from "lucide-react";
import { cmsApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const STATUS_LABELS = {
  SZKIC: "Szkic", DO_TESTOW: "Do testów", ZATWIERDZONY: "Zatwierdzony",
  OPUBLIKOWANY: "Opublikowany", ARCHIWALNY: "Archiwalny",
};
const EDITABLE = new Set(["SZKIC", "DO_TESTOW", "ZATWIERDZONY"]);

function StatusPill({ s }) {
  const color = s === "OPUBLIKOWANY" ? "bg-emerald-500/15 text-emerald-300"
    : s === "ARCHIWALNY" ? "bg-brand-elevated text-brand-muted"
    : s === "ZATWIERDZONY" ? "bg-brand-accent/15 text-brand-accent"
    : "bg-amber-500/15 text-amber-300";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`}>{STATUS_LABELS[s] || s}</span>;
}

export default function PromptsCms() {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null); // {pkg, slug, name}
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(null); // full version object
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("queue"); // queue | catalog | published
  const [queue, setQueue] = useState(null);
  const [progress, setProgress] = useState(null);
  const [published, setPublished] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filterPkg, setFilterPkg] = useState("all");
  const [filterQaPass, setFilterQaPass] = useState(false);

  const loadCatalog = () => cmsApi.catalog().then(setCatalog).catch(() => toast.error("Nie udało się wczytać katalogu."));
  const loadQueue = () => {
    cmsApi.reviewQueue().then((q) => { setQueue(q); setSel(new Set()); }).catch(() => toast.error("Nie udało się wczytać kolejki."));
    cmsApi.reviewQueueProgress().then(setProgress).catch(() => {});
  };
  const loadPublished = () => cmsApi.published().then(setPublished).catch(() => toast.error("Nie udało się wczytać opublikowanych."));
  useEffect(() => { loadCatalog(); loadQueue(); }, []);

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (items) => setSel((s) => (s.size === items.length ? new Set() : new Set(items.map((i) => i.id))));

  const runBulkAccept = async () => {
    setBulkBusy(true);
    try {
      const ids = Array.from(sel);
      const r = await cmsApi.bulkPublish(ids);
      if (r.published_count) toast.success(`Zaakceptowano (opublikowano) ${r.published_count} promptów.`);
      if (r.skipped_count) toast.error(`Pominięto ${r.skipped_count} — nie przeszły bramki jakości lub są niezmienne.`);
      setConfirmOpen(false);
      await loadQueue();
      loadCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zaakceptować zaznaczonych.");
    } finally { setBulkBusy(false); }
  };

  const openPrompt = async (pkg, slug, name) => {
    setSelected({ pkg, slug, name });
    setEditing(null);
    const r = await cmsApi.versions(pkg, slug);
    setVersions(r.versions || []);
  };

  const openVersion = async (id) => {
    const v = await cmsApi.version(id);
    setEditing(v);
  };

  const cloneCurrent = async () => {
    try {
      const v = await cmsApi.draftFromCurrent(selected.pkg, selected.slug);
      toast.success(`Utworzono nową wersję roboczą (v${v.version_no}).`);
      await openPrompt(selected.pkg, selected.slug, selected.name);
      setEditing(v);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się utworzyć wersji.");
    }
  };

  if (editing) {
    return <Editor version={editing} onBack={() => { setEditing(null); loadQueue(); loadCatalog(); if (selected) openPrompt(selected.pkg, selected.slug, selected.name); }} reloadVersion={openVersion} />;
  }

  const queueCount = queue?.total ?? 0;

  // Opcje kategorii + filtrowana lista (filtr po kategorii i „QA PASS")
  const queueCategories = queue
    ? Array.from(new Map(queue.items.map((i) => [i.package_slug, i.package_name || i.package_slug])).entries())
    : [];
  const visibleQueue = queue
    ? queue.items.filter((i) => (filterPkg === "all" || i.package_slug === filterPkg) && (!filterQaPass || i.qa_passed))
    : [];
  const currentCategoryName = filterPkg === "all"
    ? "Wszystkie kategorie"
    : (queueCategories.find(([slug]) => slug === filterPkg)?.[1] || filterPkg);
  const selectVisible = () => setSel(new Set(visibleQueue.map((i) => i.id)));
  const deselectAll = () => setSel(new Set());
  const batchProgress = progress
    ? (filterPkg === "all" ? progress.total : progress.progress?.[filterPkg])
    : null;

  return (
    <div data-testid="admin-cms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-brand-text">CMS promptów</h1>
          <p className="mt-1 text-sm text-brand-sec">Wersjonowanie i bezpieczna publikacja. Identyfikatory opublikowanych promptów są niezmienne; wersji nie usuwamy.</p>
        </div>
        <button data-testid={ADMIN.cmsNewPrompt} onClick={() => setNewOpen(true)} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on">
          <Plus size={16} /> Nowy prompt
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-2 border-b border-brand-line">
        <button
          data-testid={ADMIN.cmsQueueTab}
          onClick={() => { setTab("queue"); loadQueue(); }}
          className={`relative -mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "queue" ? "border-brand-accent text-brand-text" : "border-transparent text-brand-muted hover:text-brand-text"}`}
        >
          <ShieldCheck size={15} /> Do akceptacji
          {queueCount > 0 && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[11px] font-bold text-amber-300">{queueCount}</span>}
        </button>
        <button
          data-testid={ADMIN.cmsCatalogTab}
          onClick={() => setTab("catalog")}
          className={`relative -mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "catalog" ? "border-brand-accent text-brand-text" : "border-transparent text-brand-muted hover:text-brand-text"}`}
        >
          <FileText size={15} /> Katalog
        </button>
        <button
          data-testid={ADMIN.cmsPublishedTab}
          onClick={() => { setTab("published"); loadPublished(); }}
          className={`relative -mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "published" ? "border-brand-accent text-brand-text" : "border-transparent text-brand-muted hover:text-brand-text"}`}
        >
          <CheckCircle2 size={15} /> Opublikowane
          {published?.total ? <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-300">{published.total}</span> : null}
        </button>
      </div>

      {tab === "published" ? (
        <div data-testid={ADMIN.cmsPublishedList} className="mt-5">
          {!published ? <p className="text-sm text-brand-muted">Ładowanie…</p> : published.items.length === 0 ? (
            <p className="rounded-panel border border-brand-line bg-brand-surface px-6 py-10 text-center text-sm text-brand-muted">Brak opublikowanych promptów.</p>
          ) : (
            <div className="overflow-hidden rounded-panel border border-brand-line bg-brand-surface">
              <table className="w-full text-sm">
                <thead className="border-b border-brand-line text-left text-xs uppercase text-brand-muted">
                  <tr>
                    <th className="px-4 py-3">Nazwa</th>
                    <th className="px-4 py-3">Kolekcja</th>
                    <th className="px-4 py-3">Data publikacji</th>
                    <th className="px-4 py-3">Sposób</th>
                  </tr>
                </thead>
                <tbody>
                  {published.items.map((it) => (
                    <tr key={it.slug} className="border-b border-brand-line/60 last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-brand-text">{it.name}</div>
                        <div className="text-xs text-brand-muted">{it.slug}{it.version_no ? ` · v${it.version_no}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-brand-sec">{it.package_name}</td>
                      <td className="px-4 py-3 text-brand-sec">{it.published_at ? new Date(it.published_at).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                      <td className="px-4 py-3">
                        {it.method === "bulk"
                          ? <span className="rounded-full bg-brand-accent/15 px-2 py-0.5 text-[11px] font-semibold text-brand-accent">Masowo</span>
                          : it.method === "single"
                          ? <span className="rounded-full bg-brand-elevated px-2 py-0.5 text-[11px] font-semibold text-brand-sec">Pojedynczo</span>
                          : <span className="text-xs text-brand-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : tab === "queue" ? (
        <div data-testid={ADMIN.cmsReviewQueue} className="mt-5">
          {!queue ? <p className="text-sm text-brand-muted">Ładowanie…</p> : queue.items.length === 0 ? (
            <div className="rounded-panel border border-brand-line bg-brand-surface px-6 py-10 text-center">
              <ShieldCheck size={28} className="mx-auto text-brand-muted" />
              <p className="mt-3 text-sm text-brand-text">Brak promptów czekających na akceptację.</p>
              <p className="mt-1 text-sm text-brand-muted">Wersje pojawią się tu, gdy powstaną szkice lub trafią do statusu „Do testów". Utwórz nową wersję w zakładce „Katalog".</p>
            </div>
          ) : (
            <>
            {/* Pasek filtrów */}
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-panel border border-brand-line bg-brand-surface px-4 py-3">
              <label className="text-xs font-semibold text-brand-muted" htmlFor="queue-filter-cat">Kategoria</label>
              <select
                id="queue-filter-cat"
                data-testid="cms-queue-filter-category"
                value={filterPkg}
                onChange={(e) => { setFilterPkg(e.target.value); setSel(new Set()); }}
                className="rounded-button border border-brand-line bg-brand-elevated px-3 py-1.5 text-sm text-brand-text"
              >
                <option value="all">Wszystkie kategorie</option>
                {queueCategories.map(([slug, name]) => (
                  <option key={slug} value={slug}>{name}</option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-brand-sec cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cms-queue-filter-qa"
                  checked={filterQaPass}
                  onChange={(e) => { setFilterQaPass(e.target.checked); setSel(new Set()); }}
                />
                Tylko QA PASS
              </label>
              <span data-testid="cms-queue-visible-count" className="ml-auto text-sm font-semibold text-brand-text">
                Widoczne: {visibleQueue.length} z {queue.items.length}
              </span>
              <button
                data-testid="cms-queue-select-visible"
                onClick={selectVisible}
                disabled={visibleQueue.length === 0}
                className="rounded-button border border-brand-accent/50 px-3 py-1.5 text-xs font-semibold text-brand-accent hover:bg-brand-accent/10 disabled:opacity-40"
              >
                Zaznacz widoczne wersje ({visibleQueue.length})
              </button>
              <button
                data-testid="cms-queue-deselect-all"
                onClick={deselectAll}
                disabled={sel.size === 0}
                className="rounded-button border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-muted hover:text-brand-text disabled:opacity-40"
              >
                Odznacz wszystkie
              </button>
            </div>
            {batchProgress && (batchProgress.batch_total + batchProgress.published_before) > 0 && (
              <div data-testid="cms-batch-progress" className="mb-3 rounded-panel border border-brand-line bg-brand-surface px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-brand-text">
                    Publikacja tej partii: {batchProgress.published_from_batch} z {batchProgress.batch_total} wersji
                  </span>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-brand-sec">W kolejce: <span className="font-semibold text-brand-text">{batchProgress.in_queue}</span></span>
                    <span className="text-brand-muted">Już opublikowane wcześniej: <span className="font-semibold text-brand-sec">{batchProgress.published_before}</span></span>
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-elevated">
                  <div
                    className="h-full rounded-full bg-brand-accent transition-all"
                    style={{ width: `${batchProgress.batch_total ? Math.round((batchProgress.published_from_batch / batchProgress.batch_total) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}
            {visibleQueue.length === 0 ? (
              <div className="rounded-panel border border-brand-line bg-brand-surface px-6 py-8 text-center text-sm text-brand-muted">
                Brak wersji pasujących do wybranych filtrów.
              </div>
            ) : (
            <>
            {sel.size > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-panel border border-brand-accent/40 bg-brand-accent/10 px-4 py-3">
                <span className="text-sm font-semibold text-brand-text">Zaznaczono {sel.size} {sel.size === 1 ? "prompt" : "promptów"}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSel(new Set())} className="rounded-button px-3 py-1.5 text-xs font-medium text-brand-muted hover:text-brand-text">Wyczyść</button>
                  <button
                    data-testid={ADMIN.cmsBulkAcceptButton}
                    onClick={() => setConfirmOpen(true)}
                    className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on"
                  >
                    <CheckCircle2 size={15} /> Akceptuj zaznaczone ({sel.size})
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-hidden rounded-panel border border-brand-line bg-brand-surface">
              <table className="w-full text-sm">
                <thead className="border-b border-brand-line text-left text-xs uppercase text-brand-muted">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        data-testid={ADMIN.cmsQueueSelectAll}
                        aria-label="Zaznacz wszystkie"
                        checked={sel.size === visibleQueue.length && visibleQueue.length > 0}
                        onChange={() => toggleAll(visibleQueue)}
                      />
                    </th>
                    <th className="px-4 py-3">Prompt</th>
                    <th className="px-4 py-3">Pakiet</th>
                    <th className="px-4 py-3">Materiały</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Bramka jakości</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQueue.map((it) => (
                    <tr key={it.id} data-testid={ADMIN.cmsQueueRow} className={`border-b border-brand-line/60 last:border-0 hover:bg-brand-elevated/40 ${sel.has(it.id) ? "bg-brand-accent/5" : ""}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          data-testid={ADMIN.cmsQueueRowCheck}
                          aria-label={`Zaznacz ${it.name}`}
                          checked={sel.has(it.id)}
                          onChange={() => toggleSel(it.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-brand-text">{it.name}</div>
                        <div className="text-xs text-brand-muted">v{it.version_no}{it.is_new ? " · nowy" : ""} · {it.author_email}</div>
                      </td>
                      <td className="px-4 py-3 text-brand-sec">{it.package_name || it.package_slug}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-brand-elevated px-2 py-0.5 text-[11px] text-brand-sec">{it.input_requirement || "TEXT_ONLY"}</span></td>
                      <td className="px-4 py-3"><StatusPill s={it.status} /></td>
                      <td className="px-4 py-3">
                        {it.qa_passed
                          ? <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 size={14} /> gotowe</span>
                          : <span className="inline-flex items-center gap-1 text-amber-400"><XCircle size={14} /> {it.qa_failed_count} do poprawy</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openVersion(it.id)} className="rounded-button border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-text hover:border-brand-accent/60">
                          Przejrzyj i akceptuj
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
            </>
          )}
        </div>
      ) : (

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_1.4fr]">
        {/* Catalog */}
        <div data-testid={ADMIN.cmsCatalog} className="rounded-panel border border-brand-line bg-brand-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-sm font-semibold text-brand-text">Katalog</h2>
            <button onClick={loadCatalog} className="text-brand-muted hover:text-brand-text"><RefreshCw size={15} /></button>
          </div>
          {!catalog ? <p className="text-sm text-brand-muted">Ładowanie…</p> : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {catalog.packages.map((p) => (
                <div key={p.slug}>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">{p.name} · {p.prompt_count}</p>
                  <ul className="mt-1.5 space-y-1">
                    {p.prompts.map((pr) => (
                      <li key={pr.slug}>
                        <button
                          data-testid={ADMIN.cmsPromptRow}
                          onClick={() => openPrompt(p.slug, pr.slug, pr.name)}
                          className={`flex w-full items-center justify-between gap-2 rounded-field border px-3 py-2 text-left text-sm transition-colors ${
                            selected?.slug === pr.slug ? "border-brand-accent/60 bg-brand-elevated" : "border-brand-line hover:border-brand-accent/40"
                          }`}
                        >
                          <span className="min-w-0 flex-1 truncate text-brand-text">{pr.name}</span>
                          {pr.cms_managed && <Lock size={12} className="shrink-0 text-brand-accent" />}
                          {pr.latest_status && <StatusPill s={pr.latest_status} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Versions */}
        <div className="rounded-panel border border-brand-line bg-brand-surface p-4">
          {!selected ? (
            <p className="text-sm text-brand-muted">Wybierz prompt z katalogu, aby zobaczyć jego wersje.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <h2 className="truncate font-display text-base font-semibold text-brand-text">{selected.name}</h2>
                  <p className="text-xs text-brand-muted">{selected.pkg} / {selected.slug}</p>
                </div>
                <button data-testid={ADMIN.cmsNewVersion} onClick={cloneCurrent} className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-xs font-semibold text-brand-text hover:border-brand-accent/60">
                  <Plus size={14} /> Nowa wersja z opublikowanej
                </button>
              </div>
              <ul className="mt-4 space-y-2">
                {versions.length === 0 && <p className="text-sm text-brand-muted">Brak wersji. Utwórz nową wersję z opublikowanej.</p>}
                {versions.map((v) => (
                  <li key={v.id}>
                    <button data-testid={ADMIN.cmsVersionRow} onClick={() => openVersion(v.id)} className="flex w-full items-center justify-between gap-3 rounded-field border border-brand-line px-3 py-2.5 text-left hover:border-brand-accent/50">
                      <span className="flex items-center gap-2 text-sm text-brand-text">
                        <span className="font-semibold">v{v.version_no}</span>
                        <StatusPill s={v.status} />
                      </span>
                      <span className="text-xs text-brand-muted">{v.author_email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
      )}

      {newOpen && <NewPromptDialog packages={catalog?.packages || []} onClose={() => setNewOpen(false)} onCreated={(v) => { setNewOpen(false); setSelected({ pkg: v.package_slug, slug: v.prompt_slug, name: v.content?.name || v.prompt_slug }); setEditing(v); }} />}

      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => !bulkBusy && setConfirmOpen(false)}>
          <div data-testid={ADMIN.cmsBulkConfirmDialog} className="w-full max-w-md rounded-panel border border-brand-line bg-brand-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-brand-accent">
              <CheckCircle2 size={20} />
              <h3 className="font-display text-lg font-semibold text-brand-text">Potwierdź akceptację</h3>
            </div>
            <p className="mt-3 text-sm text-brand-sec">
              Zaraz zaakceptujesz i opublikujesz <span className="font-semibold text-brand-text">{sel.size} {sel.size === 1 ? "wersję" : "wersji"}</span> z kategorii <span className="font-semibold text-brand-text">„{currentCategoryName}"</span>. Opublikowane wersje stają się niezmienne i widoczne dla klientów.
            </p>
            <p className="mt-2 text-xs text-brand-muted">Pominięte zostaną tylko pozycje, które nie przejdą bramki jakości.</p>
            <div className="mt-5 flex gap-2">
              <button data-testid={ADMIN.cmsBulkCancelButton} disabled={bulkBusy} onClick={() => setConfirmOpen(false)} className="flex-1 rounded-button border border-brand-line px-4 py-2.5 text-sm text-brand-text disabled:opacity-50">Anuluj</button>
              <button data-testid={ADMIN.cmsBulkConfirmButton} disabled={bulkBusy} onClick={runBulkAccept} className="flex-1 inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on disabled:opacity-50">
                {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Akceptuj {sel.size}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewPromptDialog({ packages, onClose, onCreated }) {
  const [pkg, setPkg] = useState(packages[0]?.slug || "");
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const submit = async () => {
    try {
      const v = await cmsApi.newPrompt({ package_slug: pkg, prompt_slug: slug.trim(), content: { name, fields: [], template: "", repair_template: "", capabilities: {} } });
      toast.success("Utworzono szkic nowego promptu.");
      onCreated(v);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się utworzyć promptu.");
    }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-panel border border-brand-line bg-brand-surface p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-semibold text-brand-text">Nowy prompt (szkic)</h3>
        <label className="mt-4 block text-xs font-semibold text-brand-sec">Pakiet</label>
        <select value={pkg} onChange={(e) => setPkg(e.target.value)} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text">
          {packages.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
        <label className="mt-3 block text-xs font-semibold text-brand-sec">Identyfikator (slug, niezmienny po publikacji)</label>
        <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="np. nowy-prompt-2026" className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text" />
        <label className="mt-3 block text-xs font-semibold text-brand-sec">Nazwa</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text" />
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-button border border-brand-line px-4 py-2.5 text-sm text-brand-text">Anuluj</button>
          <button disabled={!slug.trim() || !name.trim()} onClick={submit} className="flex-1 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on disabled:opacity-50">Utwórz</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-brand-sec">{label}</span>
      {children}
    </label>
  );
}

function Editor({ version, onBack, reloadVersion }) {
  const [content, setContent] = useState(version.content || {});
  const [full, setFull] = useState(JSON.stringify(version.sample_values_full || {}, null, 2));
  const [required, setRequired] = useState(JSON.stringify(version.sample_values_required || {}, null, 2));
  const [lock, setLock] = useState(version.lock_version);
  const [status, setStatus] = useState(version.status);
  const [reason, setReason] = useState("");
  const [qa, setQa] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const readOnly = !EDITABLE.has(status);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setField = (i, k, v) => setContent((c) => {
    const fields = [...(c.fields || [])];
    fields[i] = { ...fields[i], [k]: v };
    return { ...c, fields };
  });

  const parseJson = (s, name) => {
    try { return JSON.parse(s || "{}"); } catch { toast.error(`Niepoprawny JSON w polu „${name}".`); throw new Error("json"); }
  };

  const save = async () => {
    let sf, sr;
    try { sf = parseJson(full, "Pełny zestaw"); sr = parseJson(required, "Tylko wymagane"); } catch { return; }
    setBusy(true);
    try {
      const v = await cmsApi.save(version.id, { lock_version: lock, content, sample_values_full: sf, sample_values_required: sr, reason });
      setLock(v.lock_version);
      setContent(v.content);
      toast.success("Zapisano wersję.");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) toast.error(typeof d === "object" ? d.message : "Konflikt zapisu — odśwież wersję.");
      else toast.error(typeof d === "string" ? d : "Nie udało się zapisać.");
    } finally { setBusy(false); }
  };

  const autofill = async () => {
    const r = await cmsApi.autofill(version.id);
    setFull(JSON.stringify(r.sample_values_full, null, 2));
    setRequired(JSON.stringify(r.sample_values_required, null, 2));
    toast("Wygenerowano propozycję danych testowych — sprawdź i zapisz.");
  };

  const runQa = async () => { const r = await cmsApi.qa(version.id); setQa(r); if (!r.passed) toast.error("Bramka jakości: są niespełnione warunki."); else toast.success("Bramka jakości: wszystkie warunki spełnione."); };
  const runPreview = async () => { const r = await cmsApi.preview(version.id); setPreview(r); };

  const changeStatus = async (s) => {
    try { await cmsApi.setStatus(version.id, { status: s, reason }); setStatus(s); toast.success(`Status: ${STATUS_LABELS[s]}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Nie udało się zmienić statusu."); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await cmsApi.publish(version.id);
      toast.success("Opublikowano wersję. Rekord katalogu został zaktualizowany.");
      reloadVersion(version.id).then((v) => v);
      setStatus("OPUBLIKOWANY");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 422 && d?.checks) { setQa({ checks: d.checks, passed: false }); toast.error("Publikacja zablokowana — bramka jakości niespełniona."); }
      else toast.error(typeof d === "string" ? d : "Nie udało się opublikować.");
    } finally { setBusy(false); }
  };

  const fields = content.fields || [];

  return (
    <div data-testid={ADMIN.cmsEditor}>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-text"><ArrowLeft size={16} /> Wróć do wersji</button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-semibold text-brand-text">v{version.version_no} · {version.prompt_slug}</h1>
        <StatusPill s={status} />
        {readOnly && <span className="inline-flex items-center gap-1 text-xs text-brand-muted"><Lock size={12} /> Wersja niezmienna</span>}
      </div>
      <p className="mt-1 text-xs text-brand-muted">Pakiet/identyfikator: {version.package_slug} / {version.prompt_slug} (niezmienny)</p>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          {/* content */}
          <div className="rounded-panel border border-brand-line bg-brand-surface p-5 space-y-4">
            <h2 className="font-display text-sm font-semibold text-brand-text">Treść promptu</h2>
            <Field label="Nazwa"><input disabled={readOnly} value={content.name || ""} onChange={(e) => set("name", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text disabled:opacity-60" /></Field>
            <Field label="Korzyść dla użytkownika"><input disabled={readOnly} value={content.short_benefit || ""} onChange={(e) => set("short_benefit", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text disabled:opacity-60" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tryb (mode)"><input disabled={readOnly} value={content.mode || ""} onChange={(e) => set("mode", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text disabled:opacity-60" /></Field>
              <Field label="Wymaganie materiałowe (inputRequirement)">
                <select disabled={readOnly} value={content.input_requirement || "TEXT_ONLY"} onChange={(e) => set("input_requirement", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text disabled:opacity-60">
                  {["TEXT_ONLY", "SOURCE_IMAGE", "IMAGE_AND_TEXT", "MULTIPLE_IMAGES", "SOURCE_DOCUMENT"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-brand-text">
              <input type="checkbox" disabled={readOnly} checked={!!(content.capabilities || {}).requiresImage} onChange={(e) => set("capabilities", { ...(content.capabilities || {}), requiresImage: e.target.checked })} />
              Prompt wymaga zdjęcia (requiresImage)
            </label>
            <Field label="Szablon główny (użyj {{id_pola}} oraz {{#if id}}…{{/if}})"><textarea disabled={readOnly} value={content.template || ""} onChange={(e) => set("template", e.target.value)} rows={7} className="w-full rounded-field border border-brand-line bg-brand-bg p-3 text-[13px] text-brand-text disabled:opacity-60" /></Field>
            <Field label="Indywidualna instrukcja poprawy"><textarea disabled={readOnly} value={content.repair_template || ""} onChange={(e) => set("repair_template", e.target.value)} rows={5} className="w-full rounded-field border border-brand-line bg-brand-bg p-3 text-[13px] text-brand-text disabled:opacity-60" /></Field>
          </div>

          {/* fields editor */}
          <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
            <h2 className="font-display text-sm font-semibold text-brand-text">Formularz (pola)</h2>
            <div className="mt-3 space-y-4">
              {fields.map((f, i) => (
                <div key={f.id || i} className="rounded-card border border-brand-line bg-brand-bg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-brand-accent">{f.id}</span>
                    <div className="flex items-center gap-3 text-xs text-brand-sec">
                      <label className="flex items-center gap-1"><input type="checkbox" disabled={readOnly} checked={!!f.required} onChange={(e) => setField(i, "required", e.target.checked)} /> wymagane</label>
                      <span>kolejność <input type="number" disabled={readOnly} value={f.order ?? i} onChange={(e) => setField(i, "order", Number(e.target.value))} className="w-14 rounded border border-brand-line bg-brand-surface px-1 py-0.5 disabled:opacity-60" /></span>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Field label="Etykieta (prosty język)"><input disabled={readOnly} value={f.label || ""} onChange={(e) => setField(i, "label", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-surface px-2 py-1.5 text-sm text-brand-text disabled:opacity-60" /></Field>
                    <Field label="Typ pola"><select disabled={readOnly} value={f.component || "text"} onChange={(e) => setField(i, "component", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-surface px-2 py-1.5 text-sm text-brand-text disabled:opacity-60">{["text", "textarea", "select", "multiselect", "tags", "number", "checkbox", "url", "date", "color"].map((x) => <option key={x}>{x}</option>)}</select></Field>
                  </div>
                  <Field label="Opis / wyjaśnienie (prosty język)"><input disabled={readOnly} value={f.help || ""} onChange={(e) => setField(i, "help", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-surface px-2 py-1.5 text-sm text-brand-text disabled:opacity-60" /></Field>
                  <Field label="Przykład (placeholder)"><input disabled={readOnly} value={f.placeholder || ""} onChange={(e) => setField(i, "placeholder", e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-surface px-2 py-1.5 text-sm text-brand-text disabled:opacity-60" /></Field>
                </div>
              ))}
              {fields.length === 0 && <p className="text-sm text-brand-muted">Ten prompt nie ma jeszcze pól.</p>}
            </div>
          </div>

          {/* sample data */}
          <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold text-brand-text">Dane testowe (do bramki jakości)</h2>
              <button data-testid={ADMIN.cmsAutofill} disabled={readOnly} onClick={autofill} className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50"><RefreshCw size={13} /> Zaproponuj automatycznie</button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Pełny zestaw (JSON)"><textarea disabled={readOnly} value={full} onChange={(e) => setFull(e.target.value)} rows={7} className="w-full rounded-field border border-brand-line bg-brand-bg p-2 font-mono text-[12px] text-brand-text disabled:opacity-60" /></Field>
              <Field label="Tylko wymagane (JSON)"><textarea disabled={readOnly} value={required} onChange={(e) => setRequired(e.target.value)} rows={7} className="w-full rounded-field border border-brand-line bg-brand-bg p-2 font-mono text-[12px] text-brand-text disabled:opacity-60" /></Field>
            </div>
          </div>
        </div>

        {/* right: actions */}
        <div className="space-y-4 xl:sticky xl:top-4 self-start">
          <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
            <Field label="Uzasadnienie zmiany (audyt)"><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="np. prosty język etykiet" className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text" /></Field>
            <button data-testid={ADMIN.cmsSave} disabled={readOnly || busy} onClick={save} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Zapisz wersję
            </button>
            <div className="mt-3 flex gap-2">
              <button data-testid={ADMIN.cmsQa} onClick={runQa} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-xs font-semibold text-brand-text hover:border-brand-accent/60"><ShieldCheck size={14} /> Bramka jakości</button>
              <button data-testid={ADMIN.cmsPreview} onClick={runPreview} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-button border border-brand-line px-3 py-2 text-xs font-semibold text-brand-text hover:border-brand-accent/60"><Eye size={14} /> Podgląd</button>
            </div>
            {!readOnly && (
              <div className="mt-3">
                <Field label="Status">
                  <select data-testid={ADMIN.cmsStatusSelect} value={status} onChange={(e) => changeStatus(e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text">
                    {["SZKIC", "DO_TESTOW", "ZATWIERDZONY"].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </Field>
                <button data-testid={ADMIN.cmsPublish} disabled={busy} onClick={publish} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-button bg-emerald-500/90 px-4 py-2.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:opacity-50">
                  <CheckCircle2 size={15} /> Opublikuj (po bramce jakości)
                </button>
                <p className="mt-2 text-[11px] text-brand-muted">Publikacja jest zablokowana, dopóki wszystkie warunki bramki nie są spełnione.</p>
              </div>
            )}
          </div>

          {qa && (
            <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
              <div className="flex items-center gap-2">
                {qa.passed ? <CheckCircle2 size={16} className="text-emerald-400" /> : <XCircle size={16} className="text-amber-400" />}
                <h3 className="font-display text-sm font-semibold text-brand-text">Bramka jakości {qa.passed ? "— zaliczona" : "— do poprawy"}</h3>
              </div>
              <ul className="mt-3 space-y-2">
                {qa.checks.map((ch) => (
                  <li key={ch.key} className="flex items-start gap-2 text-[13px]">
                    {ch.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-400" /> : <XCircle size={14} className="mt-0.5 shrink-0 text-amber-400" />}
                    <span className={ch.ok ? "text-brand-sec" : "text-brand-text"}>{ch.label}{!ch.ok && ch.detail ? <span className="block text-xs text-amber-300/90">{ch.detail}</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview && (
            <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
              <h3 className="font-display text-sm font-semibold text-brand-text">Podgląd (dokładnie jak u klienta)</h3>
              <p className="mt-2 text-[11px] uppercase text-brand-muted">Gotowy prompt</p>
              <textarea readOnly value={preview.prompt} rows={6} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg p-2 text-[12px] text-brand-text" />
              <p className="mt-2 text-[11px] uppercase text-brand-muted">Instrukcja poprawy</p>
              <textarea readOnly value={preview.repairPrompt} rows={4} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg p-2 text-[12px] text-brand-text" />
              <p className="mt-2 text-[12px] text-brand-sec"><span className="text-brand-muted">Jak użyć:</span> {preview.usage_note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
